import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Footprints, Globe, Layers, Eye } from 'lucide-react';
import CountUp from '../../components/CountUp';

const measurementTypes = [
  {
    icon: <Footprints className="w-6 h-6" />,
    name: 'Foot Traffic Lift',
    summary: 'Show your client exactly how many people walked into their store after seeing the CTV ad.',
    points: ['Uses device location data from exposed households', 'Compared to a matched control group', 'Shown as lift % vs baseline', 'Perfect for retail, dental, HVAC, auto']
  },
  {
    icon: <Globe className="w-6 h-6" />,
    name: 'Website Visit Attribution',
    summary: 'Connect CTV exposure to digital intent — even without a click.',
    points: ['Tracks site visits from exposed households', 'No click required — TV-to-web journey captured', 'UTM-free attribution model', 'Ideal for real estate, e-commerce, services']
  },
  {
    icon: <Layers className="w-6 h-6" />,
    name: 'Incremental Reach',
    summary: "Show clients who they reached on CTV that they weren't already reaching on Facebook or Google.",
    points: ['Audience overlap analysis vs existing channels', 'Justifies CTV as a new channel investment', 'Visualised as Venn diagram in report', 'Powerful for clients already running Meta/Google']
  },
  {
    icon: <Eye className="w-6 h-6" />,
    name: 'Attention & Completion',
    summary: 'Know if your ad was actually watched — not just served. Second-by-second viewability data.',
    points: ['94% average completion rate across MarqiCast', 'TVision integration — industry-leading accuracy', 'Attention quality score per placement', 'Competitive benchmark included per vertical']
  }
];

const reportMetrics = [
  { value: '12,400', label: 'Households reached within 10mi of your location' },
  { value: '4.2×', label: 'Avg. frequency per household this month' },
  { value: '94%', label: 'Video completion — watched your full 30-sec ad' },
  { value: '+22%', label: 'Website visits lifted during campaign vs prior period' }
];

const reportDifferentiators = [
  'White-labeled with your agency logo and colors',
  'Plain-English summaries — no jargon, no raw CPMs',
  'Business-outcome framing: reach, visits, calls, lift',
  'Monthly AI-generated insights and optimisation recs',
  'Competitive benchmarks by vertical included'
];

const caseStudies = [
  {
    title: 'Regional Dental Group',
    overview: '6-location practice · Southern California · $4,200/mo · 90 days',
    results: [
      { value: '14,800', label: 'Households reached within 8mi of each location' },
      { value: '+31%', label: 'New patient appointment requests' },
      { value: '94%', label: 'Video completion rate' },
      { value: '4.8×', label: 'Return on ad spend' }
    ],
    note: 'Agency margin: ~$1,400/mo · Client renewed for 12 months'
  },
  {
    title: 'Local HVAC Company',
    overview: 'Phoenix, AZ · $2,800/mo · 60-day summer campaign',
    results: [
      { value: '22,400', label: 'Households reached — homeowners, 15mi radius' },
      { value: '+44%', label: 'Inbound call volume vs same period prior year' },
      { value: '3.6×', label: 'Verified ROAS via call tracking attribution' },
      { value: '+28%', label: 'Website traffic lift during campaign window' }
    ],
    note: 'Agency margin: ~$840/mo · Upsold to $4,500/mo in month 3'
  }
];

const CastMeasurementPage = () => {
  return (
    <div className="min-h-screen" data-testid="cast-measurement-page">
      <Helmet><title>MarqiCast — Measurement & Proof of ROI</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-measurement-hero-title">
            Stop Guessing.
            <br /><span className="text-[var(--accent-primary)]">Start Proving TV Works.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            The biggest reason agencies lose CTV clients is they can't prove ROI. MarqiCast is built
            around measurement — not as an afterthought, but as the product. Every campaign ships with
            proof your clients can read, understand, and share with their business owner.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cast-measurement-cta-primary">
              See a Sample Report
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary">Book Strategy Session</Link>
          </div>
        </div>
      </section>

      {/* 4 measurement types */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Four Ways to Prove Your Campaign Worked.</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {measurementTypes.map((m, i) => (
              <div key={m.name} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mr-4">{m.icon}</div>
                  <h3 className="h3 text-[var(--text-primary)]">{m.name}</h3>
                </div>
                <p className="body-md mb-6">{m.summary}</p>
                <ul className="space-y-3">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-sm">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The report mockup */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Reports Your Clients Actually Understand — Under Your Brand.</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Mock report card */}
            <div className="feature-card bg-[var(--bg-secondary)]">
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-[var(--border-subtle)]">
                <span className="text-sm font-semibold text-[var(--text-primary)]">Campaign Summary — <span className="text-[var(--accent-primary)]">[Your Agency Name]</span></span>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {reportMetrics.map((r) => (
                  <div key={r.label} className="p-5 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)]">
                    <CountUp value={r.value} className="block text-3xl font-bold text-[var(--accent-primary)] mb-2" />
                    <p className="text-xs text-[var(--text-muted)]">{r.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Differentiators */}
            <div>
              <h3 className="h3 text-[var(--text-primary)] mb-6">What Makes It Different</h3>
              <ul className="space-y-4 mb-8">
                {reportDifferentiators.map((d) => (
                  <li key={d} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{d}</span>
                  </li>
                ))}
              </ul>
              <blockquote className="border-l-2 border-[var(--accent-primary)] pl-6 italic text-[var(--text-secondary)]">
                "The report pays for itself in client retention."
                <span className="block not-italic text-sm text-[var(--text-muted)] mt-2">— Agency partner</span>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">What Measurement Looks Like in Practice.</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {caseStudies.map((c, i) => (
              <div key={c.title} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                <h3 className="h3 text-[var(--text-primary)] mb-2">Case Study: {c.title}</h3>
                <p className="text-sm text-[var(--text-muted)] mb-8">{c.overview}</p>
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {c.results.map((r) => (
                    <div key={r.label}>
                      <CountUp value={r.value} className="block text-3xl font-bold text-[var(--accent-primary)] mb-2" />
                      <p className="text-xs text-[var(--text-muted)]">{r.label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[var(--accent-primary)] pt-4 border-t border-[var(--border-subtle)]">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container">
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Proof That Renews Clients and Grows Budgets.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">In your strategy session, we'll show you a sample report for your client's vertical — so you know exactly what you'll be presenting before the campaign even starts.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Book Your Strategy Session<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/contact" className="btn-secondary">See Pricing Plans</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastMeasurementPage;
